import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AwardType, CreateNominationInput, NominationKind, TeamMember } from '../../types/nomination';
import { teamNomineeId, uniqueTeamMembers } from '../../types/nominationValidation';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { notifyNominationSubmitted } from '../../services/inAppNotification';
import { emailNominationSubmitted } from '../../services/emailNotification';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { isDemoMode } from '../../services/mockData';
import { loadAllUsers, getReviewerAndAdminEmails } from '../../services/userLoader';
import { isCurrentEmployee, todayIso } from '../../services/employmentStatus';
import './CreateNomination.css';

// --- SPOT Award form data ---
interface SpotFormData {
  taskDescription: string;
  problemType: { operational: boolean; technical: boolean; processRelated: boolean; clientFacing: boolean };
  measurableImpact: {
    reducedManualEffort: boolean; improvedStability: boolean; automatedProcess: boolean;
    reducedIncidents: boolean; improvedMonitoring: boolean; clientAppreciation: boolean;
    reducedCost: boolean; fasterTurnaround: boolean;
  };
  beyondNormalResponsibility: 'yes' | 'no' | '';
  beyondExplanation: string;
  clearOwnership: 'yes' | 'partially' | 'no' | '';
  ownershipExample: string;
  clientAppreciation: 'yes' | 'no' | '';
}

// --- Quarterly Award form data ---
interface QuarterlyFormData {
  majorInitiative: string;
  scaleOfImpact: {
    entireTeam: boolean; multipleTeams: boolean; clientWide: boolean;
    businessLevel: boolean; revenueImpact: boolean; longTermStrategic: boolean;
  };
  measurableOutcomes: string;
  leadershipQualities: {
    mentoredJuniors: boolean; ledIndependently: boolean; crossTeamCollaboration: boolean;
    accountabilityDuringCrisis: boolean; improvedProcessStandards: boolean;
  };
  sustainableImpact: 'yes' | 'no' | '';
  sustainableExplanation: string;
  standOutReason: string;
}

const initialSpotForm: SpotFormData = {
  taskDescription: '',
  problemType: { operational: false, technical: false, processRelated: false, clientFacing: false },
  measurableImpact: {
    reducedManualEffort: false, improvedStability: false, automatedProcess: false,
    reducedIncidents: false, improvedMonitoring: false, clientAppreciation: false,
    reducedCost: false, fasterTurnaround: false,
  },
  beyondNormalResponsibility: '',
  beyondExplanation: '',
  clearOwnership: '',
  ownershipExample: '',
  clientAppreciation: '',
};

const initialQuarterlyForm: QuarterlyFormData = {
  majorInitiative: '',
  scaleOfImpact: {
    entireTeam: false, multipleTeams: false, clientWide: false,
    businessLevel: false, revenueImpact: false, longTermStrategic: false,
  },
  measurableOutcomes: '',
  leadershipQualities: {
    mentoredJuniors: false, ledIndependently: false, crossTeamCollaboration: false,
    accountabilityDuringCrisis: false, improvedProcessStandards: false,
  },
  sustainableImpact: '',
  sustainableExplanation: '',
  standOutReason: '',
};

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
}

const CreateNomination: React.FC<{ onComplete?: () => void; embedded?: boolean }> = ({ onComplete, embedded }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const isEditMode = !!id;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step management: 1=Select Employee, 2=Select Award, 3=Award Form
  const [step, setStep] = useState(1);

  /*
   * Individual or Team, chosen before anybody is picked.
   *
   * Defaults to Individual rather than to nothing. The step is "who would you like to nominate",
   * and every nomination before this was an individual one — opening on an unanswered question
   * would make the common case slower to serve no purpose. "Required" is still honoured: the
   * field always has a valid answer, so it can never be submitted without one.
   */
  const [nominationType, setNominationType] = useState<NominationKind>(NominationKind.INDIVIDUAL);
  const [teamName, setTeamName] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const isTeam = nominationType === NominationKind.TEAM;

  // Nominee state
  const [nomineeId, setNomineeId] = useState('');
  const [nomineeName, setNomineeName] = useState('');
  const [awardType, setAwardType] = useState<AwardType | ''>('');
  const [nomineePhotoUrl, setNomineePhotoUrl] = useState('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Award-specific form data
  const [spotForm, setSpotForm] = useState<SpotFormData>(initialSpotForm);
  const [quarterlyForm, setQuarterlyForm] = useState<QuarterlyFormData>(initialQuarterlyForm);

  // User search state
  const [allUsers, setAllUsers] = useState<{ id: string; email: string; name: string; department: string; role: string; managerEmail?: string; designation?: string; employeeId?: string; location?: string }[]>([]);
  const [nomineeSearch, setNomineeSearch] = useState('');
  const [selectedNominee, setSelectedNominee] = useState<{ id: string; email: string; name: string; department: string; role: string; managerEmail?: string; designation?: string; employeeId?: string; location?: string } | null>(null);
  const nomineeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await loadAllUsers();
        // DEBUG: Issue 6 — Log what attributes are available from Cognito/userLoader
        if (users.length > 0) {
          console.log('[CreateNomination][Debug] First user object keys:', Object.keys(users[0]));
          console.log('[CreateNomination][Debug] Sample user:', JSON.stringify(users[0], null, 2));
        }
        /*
         * "Active" is still-employed, not merely "the Cognito account is switched on".
         *
         * Testing `enabled !== false` alone missed the ordinary way somebody leaves: HR records
         * a date of exit in User Management and nobody disables the login. That person stayed in
         * this picker, so a leaver could still be nominated for an award months after going.
         *
         * isCurrentEmployee is the one definition of the question, shared with the Shift Roster,
         * Leave Reports and loadAllActiveEmployees. It covers both routes out, and deliberately
         * keeps somebody serving notice — a future exit date is still a current employee, and
         * their last weeks are exactly when a nomination is most likely to be written.
         */
        const today = todayIso();
        const activeUsers = users.filter(u => isCurrentEmployee(u, today));
        const selectableUsers = activeUsers.filter(u => u.email !== user?.email);
        console.log('[CreateNomination] total fetched users:', activeUsers.length);
        console.log('[CreateNomination] total displayed users:', selectableUsers.length);
        setAllUsers(selectableUsers);
      } catch (e) {
        console.error('Failed to load users:', e);
      }
    };
    fetchUsers();
  }, [user]);

  const filteredUsers = allUsers.filter(u =>
    u.name.toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    (u.department || '').toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    (u.employeeId || '').toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    (u.designation || '').toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(nomineeSearch.toLowerCase()) ||
    (u.location || '').toLowerCase().includes(nomineeSearch.toLowerCase())
  );

  useEffect(() => {
    console.log('[CreateNomination] total displayed users:', filteredUsers.length, {
      loadedUsers: allUsers.length,
      search: nomineeSearch,
    });
  }, [allUsers.length, filteredUsers.length, nomineeSearch]);

  const handleSelectNominee = (u: typeof allUsers[0]) => {
    // DEBUG: Issue 6 — Log all available employee attributes
    console.log('[CreateNomination][Debug] Selected Employee Full Object:', JSON.stringify(u, null, 2));
    console.log('[CreateNomination][Debug] Available fields:', Object.keys(u).filter(k => (u as any)[k]));
    setSelectedNominee(u);
    setNomineeId(u.email);
    setNomineeName(u.name);
    setNomineeSearch('');
    setErrors(prev => { const { nomineeName, ...rest } = prev; return rest; });
  };

  const handleClearNominee = () => {
    setSelectedNominee(null);
    setNomineeId('');
    setNomineeName('');
  };

  /**
   * Add or remove one team member.
   *
   * Toggling rather than only adding, so the same row in the list both selects and deselects —
   * a picker where clicking a chosen person does nothing looks broken, and the alternative is
   * hunting for their chip to remove it.
   *
   * Duplicates cannot arise because membership is decided by id, not by position.
   */
  const toggleTeamMember = (u: typeof allUsers[0]) => {
    setTeamMembers(prev =>
      prev.some(m => m.id === u.email)
        ? prev.filter(m => m.id !== u.email)
        : [...prev, { id: u.email, name: u.name }]
    );
    setErrors(prev => { const { teamMembers: _drop, ...rest } = prev; return rest; });
  };

  const removeTeamMember = (id: string) => setTeamMembers(prev => prev.filter(m => m.id !== id));

  /** Whoever is being nominated, for the headings on steps 2 and 3 — a person, or a team. */
  const nomineeLabel = isTeam ? teamName.trim() || 'this team' : nomineeName;

  /**
   * Whether Step 1 has enough to continue, by the rules of the kind that was chosen.
   *
   * One expression rather than a check on the button and another on submit: the two drifting
   * apart is how a form ends up letting somebody past a step it will later refuse to save.
   */
  const step1Complete = isTeam
    ? teamName.trim().length > 0 && teamMembers.length > 0
    : Boolean(selectedNominee);

  /*
   * Switching kind clears the other side's answers.
   *
   * Without this, picking Team, choosing four people, then going back to Individual would submit
   * whichever individual happened to still be selected from before — and the four would sit
   * invisibly in state, ready to reappear. Errors are cleared too, so a message about a field
   * that is no longer on screen does not survive the switch.
   */
  const handleNominationTypeChange = (next: NominationKind) => {
    if (next === nominationType) return;
    setNominationType(next);
    setErrors({});
    if (next === NominationKind.TEAM) {
      handleClearNominee();
    } else {
      setTeamName('');
      setTeamMembers([]);
    }
  };

  useEffect(() => {
    if (isEditMode && id) {
      const loadNomination = async () => {
        const nomination = await NominationServiceWrapper.getNomination(id);
        if (nomination) {
          setNomineeId(nomination.nomineeId);
          setNomineeName(nomination.nomineeName);
          /*
           * A team nomination reopens as a team, with its roster.
           *
           * Restored from nominationType rather than guessed from the `team:` prefix on
           * nomineeId: the prefix is an implementation detail of how a team gets an id, and a
           * reader of this file should not have to know it to understand what is being loaded.
           */
          if (nomination.nominationType === NominationKind.TEAM) {
            setNominationType(NominationKind.TEAM);
            setTeamName(nomination.teamName || nomination.nomineeName);
            setTeamMembers(uniqueTeamMembers(nomination.teamMembers));
          }
          const matchedUser = allUsers.find(u => u.email === nomination.nomineeId);
          setSelectedNominee(matchedUser || { id: nomination.nomineeId, email: nomination.nomineeId, name: nomination.nomineeName, department: '', role: '' });
          setAwardType(nomination.awardType);
          if (nomination.nomineePhotoUrl) {
            setNomineePhotoUrl(nomination.nomineePhotoUrl);
            setPhotoPreview(nomination.nomineePhotoUrl);
          }
          // Try to restore form data from supportingDetails JSON
          if (nomination.supportingDetails) {
            try {
              const parsed = JSON.parse(nomination.supportingDetails);
              if (nomination.awardType === AwardType.SPOT) setSpotForm({ ...initialSpotForm, ...parsed });
              else if (nomination.awardType === AwardType.QUARTERLY) setQuarterlyForm({ ...initialQuarterlyForm, ...parsed });
            } catch { /* not JSON, ignore */ }
          }
          setStep(3);
        }
      };
      loadNomination();
    }
  }, [id, isEditMode, allUsers]);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, photo: 'Please upload a JPG, PNG, or WebP image.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, photo: 'Photo must be under 5MB.' }));
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    setErrors(prev => { const { photo, ...rest } = prev; return rest; });
    if (!isDemoMode()) {
      reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
      try {
        setUploadingPhoto(true);
        const ext = file.name.substring(file.name.lastIndexOf('.'));
        const key = `nominee-photos/${uuidv4()}${ext}`;
        await uploadData({ key, data: file, options: { contentType: file.type } }).result;
        setNomineePhotoUrl(key);
      } catch (err: any) {
        setErrors(prev => ({ ...prev, photo: `Upload failed: ${err?.message || 'Please try again.'}` }));
      } finally {
        setUploadingPhoto(false);
      }
    } else {
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        setPhotoPreview(dataUrl);
        setNomineePhotoUrl(dataUrl);
      };
    }
  };

  // Serialize form data into reason + supportingDetails
  const buildInput = (): CreateNominationInput => {
    let reason = '';
    let supportingDetails = '';
    if (awardType === AwardType.SPOT) {
      reason = spotForm.taskDescription;
      supportingDetails = JSON.stringify(spotForm);
    } else if (awardType === AwardType.QUARTERLY) {
      reason = quarterlyForm.majorInitiative;
      supportingDetails = JSON.stringify(quarterlyForm);
    }
    /*
     * A team fills nomineeId/nomineeName too, with the team's identity.
     *
     * Those two are what the feed, the cards, the review queue, the admin table, the emails and
     * the serializer's required-field check all read. Leaving them empty for a team would empty
     * every one of those screens; filling them means a team nomination shows up everywhere an
     * individual one does, under the team's name, with no change to any of them.
     */
    if (isTeam) {
      const members = uniqueTeamMembers(teamMembers);
      const name = teamName.trim();
      return {
        nomineeId: teamNomineeId(name),
        nomineeName: name,
        nominationType: NominationKind.TEAM,
        teamName: name,
        teamMembers: members,
        awardType: awardType as AwardType,
        reason,
        supportingDetails,
        nomineePhotoUrl: nomineePhotoUrl || undefined,
      };
    }

    return {
      nomineeId: nomineeId.trim(),
      nomineeName: nomineeName.trim(),
      nominationType: NominationKind.INDIVIDUAL,
      awardType: awardType as AwardType,
      reason,
      supportingDetails,
      nomineePhotoUrl: nomineePhotoUrl || undefined,
    };
  };

  const validateAwardForm = (): boolean => {
    const fieldErrors: Record<string, string> = {};
    if (awardType === AwardType.SPOT) {
      const wc = wordCount(spotForm.taskDescription);
      if (wc < 50) fieldErrors.taskDescription = `Minimum 50 words required (currently ${wc})`;
      if (wc > 200) fieldErrors.taskDescription = `Maximum 200 words allowed (currently ${wc})`;
      if (!Object.values(spotForm.problemType).some(Boolean)) fieldErrors.problemType = 'Select at least one problem type';
      if (!Object.values(spotForm.measurableImpact).some(Boolean)) fieldErrors.measurableImpact = 'Select at least one measurable impact';
      if (!spotForm.beyondNormalResponsibility) fieldErrors.beyondNormalResponsibility = 'This field is required';
      if (spotForm.beyondNormalResponsibility === 'yes' && !spotForm.beyondExplanation.trim()) fieldErrors.beyondExplanation = 'Please explain how';
      if (!spotForm.clearOwnership) fieldErrors.clearOwnership = 'This field is required';
      if (!spotForm.ownershipExample.trim()) fieldErrors.ownershipExample = 'Please provide a brief example';
      if (!spotForm.clientAppreciation) fieldErrors.clientAppreciation = 'This field is required';
    } else if (awardType === AwardType.QUARTERLY) {
      const wc = wordCount(quarterlyForm.majorInitiative);
      if (wc < 1) fieldErrors.majorInitiative = 'This field is required';
      if (wc > 300) fieldErrors.majorInitiative = `Maximum 300 words allowed (currently ${wc})`;
      if (!Object.values(quarterlyForm.scaleOfImpact).some(Boolean)) fieldErrors.scaleOfImpact = 'Select at least one';
      if (!quarterlyForm.measurableOutcomes.trim()) fieldErrors.measurableOutcomes = 'This field is required';
      if (!Object.values(quarterlyForm.leadershipQualities).some(Boolean)) fieldErrors.leadershipQualities = 'Select at least one';
      if (!quarterlyForm.sustainableImpact) fieldErrors.sustainableImpact = 'This field is required';
      if (quarterlyForm.sustainableImpact === 'yes' && !quarterlyForm.sustainableExplanation.trim()) fieldErrors.sustainableExplanation = 'Please explain';
      if (!quarterlyForm.standOutReason.trim()) fieldErrors.standOutReason = 'This field is required';
    }
    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleSaveDraft = async () => {
    if (!validateAwardForm()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const input = buildInput();
      const result = await NominationServiceWrapper.createNomination(input,user?.id || 'unknown',user?.name || 'Unknown User',true);
      if (result.success) { if (onComplete) onComplete(); else navigate('/nominations/my'); }
      else setSubmitError(result.errors?.[0] || 'Failed to save draft.');
    } catch (err: any) {
      setSubmitError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateAwardForm()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const input = buildInput();
      const result = await NominationServiceWrapper.createNomination(input, user?.id || 'unknown', user?.name || 'Unknown User', false);
      if (result.success) {
        // Load reviewer/admin emails for notifications
        const { reviewerEmails, adminEmails } = await getReviewerAndAdminEmails();
        notifyNominationSubmitted(reviewerEmails, adminEmails, user?.name || 'User', input.nomineeName, input.awardType, result.nomination?.id || '');
        emailNominationSubmitted(reviewerEmails, adminEmails, user?.name || 'User', input.nomineeName, input.awardType, result.nomination?.id || '');
        refresh();
        if (onComplete) onComplete(); else navigate('/nominations/my');
      } else {
        setSubmitError(result.errors?.[0] || 'Failed to submit nomination.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER ---
  return (
    <div className={`create-nom-page ${embedded ? 'create-nom-page--embedded' : ''}`}>
      <div className="create-nom-card">
        <div className="create-nom-header">
          <div className="create-nom-header-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          </div>
          <div>
            <h2>{isEditMode ? 'Edit Nomination' : 'New Award Nomination'}</h2>
            <p>{isEditMode ? 'Update the nomination details below' : 'Recognize an outstanding colleague for their contributions'}</p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="nom-steps">
          <div className={`nom-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="nom-step-number">{step > 1 ? '✓' : '1'}</div>
            <span>Select Employee</span>
          </div>
          <div className="nom-step-line" />
          <div className={`nom-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="nom-step-number">{step > 2 ? '✓' : '2'}</div>
            <span>Choose Award</span>
          </div>
          <div className="nom-step-line" />
          <div className={`nom-step ${step >= 3 ? 'active' : ''}`}>
            <div className="nom-step-number">3</div>
            <span>Nomination Details</span>
          </div>
        </div>

        <div className="create-nom-body">
          {/* ===== STEP 1: Select Employee ===== */}
          {step === 1 && (
            <div className="nom-step-content">
              {/*
                * Asked before "who", because it decides what "who" means — one person, or a team
                * and its members. Two radio cards rather than a select: there are exactly two
                * answers, both need a line of explanation, and a closed dropdown would hide the
                * choice that changes the rest of the step.
                */}
              <div className="nom-form-group nom-kind-group">
                <label className="nom-form-label" id="nom-kind-label">
                  Select Award Type <span className="required">*</span>
                </label>
                <div className="nom-kind-options" role="radiogroup" aria-labelledby="nom-kind-label">
                  {([
                    [NominationKind.INDIVIDUAL, 'Individual', 'Recognise one colleague'],
                    [NominationKind.TEAM, 'Team', 'Recognise a team and its members'],
                  ] as const).map(([kind, label, hint]) => (
                    <button
                      key={kind}
                      type="button"
                      role="radio"
                      aria-checked={nominationType === kind}
                      className={`nom-kind-option ${nominationType === kind ? 'selected' : ''}`}
                      onClick={() => handleNominationTypeChange(kind)}
                    >
                      <span className="nom-kind-option-name">{label}</span>
                      <span className="nom-kind-option-hint">{hint}</span>
                      {nominationType === kind && <span className="nom-kind-check" aria-hidden="true">{'✓'}</span>}
                    </button>
                  ))}
                </div>
              </div>

              <h3 className="nom-step-title">Who would you like to nominate?</h3>

              {isTeam && (
                <div className="nom-form-group">
                  <label className="nom-form-label" htmlFor="nom-team-name">
                    Team Name <span className="required">*</span>
                  </label>
                  <input
                    id="nom-team-name"
                    className={`nom-form-input ${errors.teamName ? 'error' : ''}`}
                    type="text"
                    value={teamName}
                    onChange={(e) => {
                      setTeamName(e.target.value);
                      setErrors(prev => { const { teamName: _drop, ...rest } = prev; return rest; });
                    }}
                    placeholder="e.g. Platform Reliability Squad"
                    autoComplete="off"
                  />
                  {errors.teamName && <span className="nom-form-error">{errors.teamName}</span>}
                </div>
              )}

              <div className="nom-employee-layout">
                {/* Nominee Selection */}
                <div className="nom-form-group nom-employee-selector" ref={nomineeDropdownRef}>
                  <div className="nom-selector-sticky">
                    <div className="nom-selector-heading">
                      <label className="nom-form-label">
                        {isTeam ? 'Select Team Members' : 'Select Employee'} <span className="required">*</span>
                      </label>
                      <span className="nom-selector-count">
                        {isTeam && teamMembers.length > 0
                          ? `${teamMembers.length} selected · ${filteredUsers.length} available`
                          : `${filteredUsers.length} available`}
                      </span>
                    </div>
                    <input
                      className={`nom-form-input ${errors.nomineeName ? 'error' : ''}`}
                      type="text"
                      value={nomineeSearch}
                      onChange={(e) => setNomineeSearch(e.target.value)}
                      placeholder="Search by name, email, ID, designation, or department..."
                      autoComplete="off"
                    />
                  </div>
                  {/*
                    * The roster, above the list it is picked from.
                    *
                    * Chips rather than a second list: the question they answer is "who is in so
                    * far", which is a glance, and a full-width row each would push the picker
                    * itself off the screen on a team of eight. Each chip removes its own member,
                    * so the list below is not the only way to undo a click.
                    */}
                  {isTeam && teamMembers.length > 0 && (
                    <div className="nom-team-chips" aria-label="Selected team members">
                      {teamMembers.map(member => (
                        <span className="nom-team-chip" key={member.id}>
                          <span className="nom-team-chip-name" title={`${member.name} (${member.id})`}>
                            {member.name}
                          </span>
                          <button
                            type="button"
                            className="nom-team-chip-remove"
                            onClick={() => removeTeamMember(member.id)}
                            aria-label={`Remove ${member.name} from the team`}
                          >
                            {'×'}
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {isTeam && errors.teamMembers && <span className="nom-form-error">{errors.teamMembers}</span>}

                  {!isTeam && selectedNominee && (
                    <div className="nom-selected-user">
                      <div className="nom-selected-user-info">
                        <div className="nom-selected-avatar">{getInitials(selectedNominee.name)}</div>
                        <div className="nom-selected-details">
                          <span className="nom-selected-name" title={selectedNominee.name}>{selectedNominee.name}</span>
                          <span className="nom-selected-role" title={selectedNominee.designation || selectedNominee.role || 'Designation not available'}>{selectedNominee.designation || selectedNominee.role || 'Designation not available'}</span>
                          <span className="nom-selected-email" title={selectedNominee.email}>{selectedNominee.email}</span>
                          {selectedNominee.employeeId && <span className="nom-selected-empid" title={selectedNominee.employeeId}>ID: {selectedNominee.employeeId}</span>}
                          {selectedNominee.department && <span className="nom-selected-dept" title={selectedNominee.department}>{selectedNominee.department}</span>}
                          {selectedNominee.managerEmail && <span className="nom-selected-manager" title={selectedNominee.managerEmail}>Manager: {selectedNominee.managerEmail}</span>}
                          {selectedNominee.location && <span className="nom-selected-location" title={selectedNominee.location}>{selectedNominee.location}</span>}
                        </div>
                      </div>
                      <button type="button" className="nom-clear-btn" onClick={handleClearNominee} aria-label={`Clear selected employee ${selectedNominee.name}`}>{'\u00D7'}</button>
                    </div>
                  )}
                  <div className="nom-user-dropdown" role="listbox" aria-label="Employee selection list">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(u => {
                        /* One list, two behaviours: a team toggles membership, an individual
                           replaces the single selection. Both read `selected` the same way, so
                           the tick means "chosen" in either mode. */
                        const selected = isTeam
                          ? teamMembers.some(m => m.id === u.email)
                          : selectedNominee?.email === u.email;
                        const roleLabel = u.designation || u.role || 'Designation not available';
                        return (
                          <button
                            key={u.email}
                            type="button"
                            className={`nom-user-option ${selected ? 'selected' : ''}`}
                            onClick={() => (isTeam ? toggleTeamMember(u) : handleSelectNominee(u))}
                            role="option"
                            aria-selected={selected}
                          >
                            <div className="nom-user-option-avatar">{getInitials(u.name)}</div>
                            <div className="nom-user-option-info">
                              <span className="nom-user-option-name" title={u.name}>{u.name}</span>
                              <span className="nom-user-option-role" title={roleLabel}>{roleLabel}</span>
                              <span className="nom-user-option-email" title={u.email}>{u.email}</span>
                              {u.employeeId && <span className="nom-user-option-empid" title={u.employeeId}>ID: {u.employeeId}</span>}
                            </div>
                            {selected && <span className="nom-user-option-check" aria-hidden="true">{'\u2713'}</span>}
                          </button>
                        );
                      })
                    ) : (
                      <div className="nom-user-no-results">No employees found</div>
                    )}
                  </div>
                  {errors.nomineeName && <span className="nom-form-error">{errors.nomineeName}</span>}
                </div>

                {/* Photo upload is optional; cards and feeds fall back to initials when no image is provided. */}
                <div className="nom-photo-section">
                  <label className="nom-form-label">Nominee/Team Photo <span className="nom-optional-label">Optional</span></label>
                  <div className="nom-photo-upload" onClick={() => fileInputRef.current?.click()}>
                    {photoPreview ? (
                      <img src={photoPreview} alt="Nominee/Team" className="nom-photo-preview" />
                    ) : (
                      <div className="nom-photo-placeholder">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                        <span>{uploadingPhoto ? 'Uploading...' : 'Click to upload photo (optional)'}</span>
                      </div>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoSelect} style={{ display: 'none' }} />
                  </div>
                  {errors.photo && <span className="nom-form-error">{errors.photo}</span>}
                </div>
              </div>

              <div className="nom-step-actions">
                <button className="nom-btn nom-btn-outline" onClick={() => { if (onComplete) onComplete(); else navigate('/nominations/my'); }}>Cancel</button>
                <button className="nom-btn nom-btn-primary" disabled={!step1Complete || uploadingPhoto} onClick={() => setStep(2)}>
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP 2: Select Award Type ===== */}
          {step === 2 && (
            <div className="nom-step-content">
              <h3 className="nom-step-title">Choose Award Type for {nomineeLabel}</h3>

              <div className="nom-award-cards">
                {/* SPOT Award Card */}
                <div
                  className={`nom-award-card ${awardType === AwardType.SPOT ? 'selected' : ''}`}
                  onClick={() => setAwardType(AwardType.SPOT)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAwardType(AwardType.SPOT); }}
                >
                  <div className="nom-award-card-icon">⚡</div>
                  <h4>SPOT Award</h4>
                  <p className="nom-award-card-tagline">Fast recognition | Immediate impact | Anyone can nominate</p>
                  <div className="nom-award-card-details">
                    <div className="nom-award-eligibility">
                      <span className="nom-eligibility-label">Eligibility</span>
                      <ul>
                        <li>Can be nominated by: Leads, peers, colleagues, or cross-team members</li>
                        <li>Recognizes short-term but high-impact ownership</li>
                      </ul>
                    </div>
                  </div>
                  {awardType === AwardType.SPOT && <div className="nom-award-check">✓</div>}
                </div>

                {/* Quarterly Award Card */}
                <div
                  className={`nom-award-card ${awardType === AwardType.QUARTERLY ? 'selected' : ''}`}
                  onClick={() => setAwardType(AwardType.QUARTERLY)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAwardType(AwardType.QUARTERLY); }}
                >
                  <div className="nom-award-card-icon">🏆</div>
                  <h4>Quarterly Excellence Award</h4>
                  <p className="nom-award-card-tagline">Strategic | Bigger impact | Leadership-level contribution</p>
                  <div className="nom-award-card-details">
                    <div className="nom-award-eligibility">
                      <span className="nom-eligibility-label">Eligibility</span>
                      <ul>
                        <li>Nominated by Leads or Senior Leadership</li>
                        <li>Evaluated once per quarter</li>
                        <li>Recognizes sustained impact or large-scale contribution</li>
                      </ul>
                    </div>
                  </div>
                  {awardType === AwardType.QUARTERLY && <div className="nom-award-check">✓</div>}
                </div>
              </div>

              {/* Comparison Table */}
              <div className="nom-comparison-table">
                <h4>Spot vs Quarterly — At a Glance</h4>
                <table>
                  <thead>
                    <tr><th>Spot Award</th><th>Quarterly Award</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Fast recognition</td><td>Structured quarterly recognition</td></tr>
                    <tr><td>Task-level impact</td><td>Strategic / wider impact</td></tr>
                    <tr><td>Short-term measurable gain</td><td>Long-term measurable gain</td></tr>
                    <tr><td>Anyone can nominate</td><td>Lead / Leadership nomination</td></tr>
                    <tr><td>Tactical ownership</td><td>Strategic ownership</td></tr>
                  </tbody>
                </table>
              </div>

              {errors.awardType && <span className="nom-form-error">{errors.awardType}</span>}

              <div className="nom-step-actions">
                <button className="nom-btn nom-btn-outline" onClick={() => setStep(1)}>← Back</button>
                <button className="nom-btn nom-btn-primary" disabled={!awardType} onClick={() => setStep(3)}>
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Award-Specific Form ===== */}
          {step === 3 && awardType === AwardType.SPOT && (
            <div className="nom-step-content">
              <div className="nom-form-banner spot">
                <span className="nom-form-banner-icon">⚡</span>
                <div>
                  <h3>SPOT Award Nomination</h3>
                  <p>Nominating: <strong>{nomineeLabel}</strong></p>
                </div>
              </div>

              <div className="nom-award-standard">
                <h4>Spot Award Standard</h4>
                <p>A Spot Award is given when someone: Took full ownership · Delivered independently · Created measurable improvement · Solved a real problem · Made the team's life easier</p>
              </div>

              {/* Q1: Task/Initiative */}
              <div className="nom-form-group">
                <label className="nom-form-label">1. What was the specific task / initiative owned? <span className="required">*</span></label>
                <p className="nom-form-hint">Brief description — min 50 words, max 200 words ({wordCount(spotForm.taskDescription)} words)</p>
                <textarea
                  className={`nom-form-textarea ${errors.taskDescription ? 'error' : ''}`}
                  value={spotForm.taskDescription}
                  onChange={(e) => setSpotForm(prev => ({ ...prev, taskDescription: e.target.value }))}
                  placeholder="Describe the specific task or initiative..."
                  rows={5}
                />
                {errors.taskDescription && <span className="nom-form-error">{errors.taskDescription}</span>}
              </div>

              {/* Q2: Problem Type */}
              <div className="nom-form-group">
                <label className="nom-form-label">2. What problem did it solve? <span className="required">*</span></label>
                <div className="nom-checkbox-group">
                  {([['operational', 'Operational'], ['technical', 'Technical'], ['processRelated', 'Process-related'], ['clientFacing', 'Client-facing']] as const).map(([key, label]) => (
                    <label key={key} className="nom-checkbox-label">
                      <input type="checkbox" checked={spotForm.problemType[key]} onChange={(e) => setSpotForm(prev => ({ ...prev, problemType: { ...prev.problemType, [key]: e.target.checked } }))} />
                      <span className="nom-checkbox-custom" />
                      {label}
                    </label>
                  ))}
                </div>
                {errors.problemType && <span className="nom-form-error">{errors.problemType}</span>}
              </div>

              {/* Q3: Measurable Impact */}
              <div className="nom-form-group">
                <label className="nom-form-label">3. What measurable impact was delivered? <span className="required">*</span></label>
                <p className="nom-form-hint">Select all that apply</p>
                <div className="nom-checkbox-group two-col">
                  {([
                    ['reducedManualEffort', 'Reduced manual effort'], ['improvedStability', 'Improved stability/reliability'],
                    ['automatedProcess', 'Automated repetitive process'], ['reducedIncidents', 'Reduced incidents'],
                    ['improvedMonitoring', 'Improved monitoring/visibility'], ['clientAppreciation', 'Client appreciation received'],
                    ['reducedCost', 'Reduced cost'], ['fasterTurnaround', 'Faster turnaround time'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="nom-checkbox-label">
                      <input type="checkbox" checked={spotForm.measurableImpact[key]} onChange={(e) => setSpotForm(prev => ({ ...prev, measurableImpact: { ...prev.measurableImpact, [key]: e.target.checked } }))} />
                      <span className="nom-checkbox-custom" />
                      {label}
                    </label>
                  ))}
                </div>
                {errors.measurableImpact && <span className="nom-form-error">{errors.measurableImpact}</span>}
              </div>

              {/* Q4: Beyond Normal Responsibility */}
              <div className="nom-form-group">
                <label className="nom-form-label">4. Was this beyond the person's normal job responsibility? <span className="required">*</span></label>
                <div className="nom-radio-group">
                  {(['yes', 'no'] as const).map(val => (
                    <label key={val} className="nom-radio-label">
                      <input type="radio" name="beyondNormal" checked={spotForm.beyondNormalResponsibility === val} onChange={() => setSpotForm(prev => ({ ...prev, beyondNormalResponsibility: val }))} />
                      <span className="nom-radio-custom" />
                      {val.charAt(0).toUpperCase() + val.slice(1)}
                    </label>
                  ))}
                </div>
                {errors.beyondNormalResponsibility && <span className="nom-form-error">{errors.beyondNormalResponsibility}</span>}
                {spotForm.beyondNormalResponsibility === 'yes' && (
                  <div className="nom-conditional-field">
                    <textarea
                      className={`nom-form-textarea ${errors.beyondExplanation ? 'error' : ''}`}
                      value={spotForm.beyondExplanation}
                      onChange={(e) => setSpotForm(prev => ({ ...prev, beyondExplanation: e.target.value }))}
                      placeholder="Explain how this was beyond normal responsibility..."
                      rows={3}
                    />
                    {errors.beyondExplanation && <span className="nom-form-error">{errors.beyondExplanation}</span>}
                  </div>
                )}
              </div>

              {/* Q5: Clear Ownership */}
              <div className="nom-form-group">
                <label className="nom-form-label">5. Did the nominee/team show clear ownership (without being chased)? <span className="required">*</span></label>
                <div className="nom-radio-group">
                  {(['yes', 'partially', 'no'] as const).map(val => (
                    <label key={val} className="nom-radio-label">
                      <input type="radio" name="clearOwnership" checked={spotForm.clearOwnership === val} onChange={() => setSpotForm(prev => ({ ...prev, clearOwnership: val }))} />
                      <span className="nom-radio-custom" />
                      {val.charAt(0).toUpperCase() + val.slice(1)}
                    </label>
                  ))}
                </div>
                {errors.clearOwnership && <span className="nom-form-error">{errors.clearOwnership}</span>}
                <div className="nom-conditional-field">
                  <textarea
                    className={`nom-form-textarea ${errors.ownershipExample ? 'error' : ''}`}
                    value={spotForm.ownershipExample}
                    onChange={(e) => setSpotForm(prev => ({ ...prev, ownershipExample: e.target.value }))}
                    placeholder="Provide a brief example..."
                    rows={3}
                  />
                  {errors.ownershipExample && <span className="nom-form-error">{errors.ownershipExample}</span>}
                </div>
              </div>

              {/* Q6: Client Appreciation */}
              <div className="nom-form-group">
                <label className="nom-form-label">6. Was there client or stakeholder appreciation? <span className="required">*</span></label>
                <div className="nom-radio-group">
                  <label className="nom-radio-label">
                    <input type="radio" name="clientAppreciation" checked={spotForm.clientAppreciation === 'yes'} onChange={() => setSpotForm(prev => ({ ...prev, clientAppreciation: 'yes' }))} />
                    <span className="nom-radio-custom" />
                    Yes (Attach proof/email if available)
                  </label>
                  <label className="nom-radio-label">
                    <input type="radio" name="clientAppreciation" checked={spotForm.clientAppreciation === 'no'} onChange={() => setSpotForm(prev => ({ ...prev, clientAppreciation: 'no' }))} />
                    <span className="nom-radio-custom" />
                    No
                  </label>
                </div>
                {errors.clientAppreciation && <span className="nom-form-error">{errors.clientAppreciation}</span>}
              </div>

              {/* Actions */}
              <div className="nom-form-actions">
                {submitError && <div className="nom-submit-error" role="alert">{submitError}</div>}
                <button className="nom-btn nom-btn-outline" onClick={() => setStep(2)}>← Back</button>
                <button className="nom-btn nom-btn-secondary" onClick={handleSaveDraft} disabled={loading || uploadingPhoto}>{loading ? 'Saving...' : 'Save as Draft'}</button>
                <button className="nom-btn nom-btn-primary" onClick={handleSubmit} disabled={loading || uploadingPhoto}>{loading ? 'Submitting...' : 'Submit Nomination'}</button>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Quarterly Excellence Award Form ===== */}
          {step === 3 && awardType === AwardType.QUARTERLY && (
            <div className="nom-step-content">
              <div className="nom-form-banner quarterly">
                <span className="nom-form-banner-icon">🏆</span>
                <div>
                  <h3>Quarterly Excellence Award Nomination</h3>
                  <p>Nominating: <strong>{nomineeLabel}</strong></p>
                </div>
              </div>

              {/* Q1: Major Initiative */}
              <div className="nom-form-group">
                <label className="nom-form-label">1. What major initiative or area did the nominee/team own this quarter? <span className="required">*</span></label>
                <p className="nom-form-hint">Explain clearly — max 300 words ({wordCount(quarterlyForm.majorInitiative)} words)</p>
                <textarea
                  className={`nom-form-textarea ${errors.majorInitiative ? 'error' : ''}`}
                  value={quarterlyForm.majorInitiative}
                  onChange={(e) => setQuarterlyForm(prev => ({ ...prev, majorInitiative: e.target.value }))}
                  placeholder="Describe the major initiative or area of ownership..."
                  rows={6}
                />
                {errors.majorInitiative && <span className="nom-form-error">{errors.majorInitiative}</span>}
              </div>

              {/* Q2: Scale of Impact */}
              <div className="nom-form-group">
                <label className="nom-form-label">2. What was the scale of impact? <span className="required">*</span></label>
                <div className="nom-checkbox-group two-col">
                  {([
                    ['entireTeam', 'Affected entire team'], ['multipleTeams', 'Affected multiple teams'],
                    ['clientWide', 'Client-wide impact'], ['businessLevel', 'Business-level impact'],
                    ['revenueImpact', 'Revenue impact'], ['longTermStrategic', 'Long-term strategic benefit'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="nom-checkbox-label">
                      <input type="checkbox" checked={quarterlyForm.scaleOfImpact[key]} onChange={(e) => setQuarterlyForm(prev => ({ ...prev, scaleOfImpact: { ...prev.scaleOfImpact, [key]: e.target.checked } }))} />
                      <span className="nom-checkbox-custom" />
                      {label}
                    </label>
                  ))}
                </div>
                {errors.scaleOfImpact && <span className="nom-form-error">{errors.scaleOfImpact}</span>}
              </div>

              {/* Q3: Measurable Outcomes */}
              <div className="nom-form-group">
                <label className="nom-form-label">3. What measurable outcomes were delivered? <span className="required">*</span></label>
                <p className="nom-form-hint">Examples: Reduced incidents by X%, Automated X processes, Reduced cost by X, Improved performance by X%, Delivered project ahead of schedule, Stabilized critical system</p>
                <textarea
                  className={`nom-form-textarea ${errors.measurableOutcomes ? 'error' : ''}`}
                  value={quarterlyForm.measurableOutcomes}
                  onChange={(e) => setQuarterlyForm(prev => ({ ...prev, measurableOutcomes: e.target.value }))}
                  placeholder="Describe measurable outcomes with specific numbers where possible..."
                  rows={5}
                />
                {errors.measurableOutcomes && <span className="nom-form-error">{errors.measurableOutcomes}</span>}
              </div>

              {/* Q4: Leadership Qualities */}
              <div className="nom-form-group">
                <label className="nom-form-label">4. Did the nominee/team demonstrate leadership qualities? <span className="required">*</span></label>
                <div className="nom-checkbox-group">
                  {([
                    ['mentoredJuniors', 'Mentored juniors'], ['ledIndependently', 'Led initiative independently'],
                    ['crossTeamCollaboration', 'Influenced cross-team collaboration'],
                    ['accountabilityDuringCrisis', 'Took accountability during crisis'],
                    ['improvedProcessStandards', 'Improved process standards'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="nom-checkbox-label">
                      <input type="checkbox" checked={quarterlyForm.leadershipQualities[key]} onChange={(e) => setQuarterlyForm(prev => ({ ...prev, leadershipQualities: { ...prev.leadershipQualities, [key]: e.target.checked } }))} />
                      <span className="nom-checkbox-custom" />
                      {label}
                    </label>
                  ))}
                </div>
                {errors.leadershipQualities && <span className="nom-form-error">{errors.leadershipQualities}</span>}
              </div>

              {/* Q5: Sustainable Impact */}
              <div className="nom-form-group">
                <label className="nom-form-label">5. Was the impact sustainable (not one-time)? <span className="required">*</span></label>
                <div className="nom-radio-group">
                  {(['yes', 'no'] as const).map(val => (
                    <label key={val} className="nom-radio-label">
                      <input type="radio" name="sustainableImpact" checked={quarterlyForm.sustainableImpact === val} onChange={() => setQuarterlyForm(prev => ({ ...prev, sustainableImpact: val }))} />
                      <span className="nom-radio-custom" />
                      {val.charAt(0).toUpperCase() + val.slice(1)}
                    </label>
                  ))}
                </div>
                {errors.sustainableImpact && <span className="nom-form-error">{errors.sustainableImpact}</span>}
                {quarterlyForm.sustainableImpact === 'yes' && (
                  <div className="nom-conditional-field">
                    <textarea
                      className={`nom-form-textarea ${errors.sustainableExplanation ? 'error' : ''}`}
                      value={quarterlyForm.sustainableExplanation}
                      onChange={(e) => setQuarterlyForm(prev => ({ ...prev, sustainableExplanation: e.target.value }))}
                      placeholder="Explain how the impact is sustainable..."
                      rows={3}
                    />
                    {errors.sustainableExplanation && <span className="nom-form-error">{errors.sustainableExplanation}</span>}
                  </div>
                )}
              </div>

              {/* Q6: Stand Out Reason */}
              <div className="nom-form-group">
                <label className="nom-form-label">6. Why does this contribution stand out from normal delivery expectations? <span className="required">*</span></label>
                <textarea
                  className={`nom-form-textarea ${errors.standOutReason ? 'error' : ''}`}
                  value={quarterlyForm.standOutReason}
                  onChange={(e) => setQuarterlyForm(prev => ({ ...prev, standOutReason: e.target.value }))}
                  placeholder="Explain what makes this contribution exceptional..."
                  rows={4}
                />
                {errors.standOutReason && <span className="nom-form-error">{errors.standOutReason}</span>}
              </div>

              {/* Actions */}
              <div className="nom-form-actions">
                {submitError && <div className="nom-submit-error" role="alert">{submitError}</div>}
                <button className="nom-btn nom-btn-outline" onClick={() => setStep(2)}>← Back</button>
                <button className="nom-btn nom-btn-secondary" onClick={handleSaveDraft} disabled={loading || uploadingPhoto}>{loading ? 'Saving...' : 'Save as Draft'}</button>
                <button className="nom-btn nom-btn-primary" onClick={handleSubmit} disabled={loading || uploadingPhoto}>{loading ? 'Submitting...' : 'Submit Nomination'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateNomination;
